import {
  JoonColorExpanded,
  JoonUIColor,
  QuestInstance,
  SPACING,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import dayjs, { Dayjs } from "dayjs"
import { useMemo } from "react"

import QuestListItem from "./QuestListItem"
import ScheduledMessageItem from "./ScheduledMessageItem"
import useRoutinesStore from "./useRoutinesStore"
import { useAssignedQuestsQuery } from "../../../networking/quests/queries"
import { useScheduledMessagesQuery } from "../../../networking/scheduledMessages/queries"
import { ScheduledMessageWithRoutine } from "../../../types/scheduledMessages"
import { createWeeklyPlanSections } from "../../../util/quests"

const RoutineDate = ({ date }: { date: Dayjs }) => {
  const { data: quests } = useAssignedQuestsQuery(date)
  const { showParentCreatedRoutines, selectedUserId } = useRoutinesStore()

  const filteredQuests = useMemo(
    () =>
      quests?.filter((quest) => {
        const isCoachCreated =
          quest.series.assigner_profile?.role === UserRole.COACH
        if (isCoachCreated) return true
        if (showParentCreatedRoutines) {
          if (!selectedUserId) return true
          return quest.series.user_id === selectedUserId
        }
        return false
      }) || [],
    [quests, showParentCreatedRoutines, selectedUserId]
  )

  const { data: scheduledMessages } = useScheduledMessagesQuery()

  const todaysMessages = scheduledMessages?.filter((message) =>
    dayjs(message.datetime).isSame(date, "day")
  )

  const orderedRoutines = useMemo(
    () => createWeeklyPlanSections(filteredQuests, todaysMessages || []),
    [filteredQuests, todaysMessages]
  )

  const dateIsToday = date.isSame(dayjs(), "day")

  return (
    <div className="flex flex-col">
      <div className="flex flex-row gap-2 border-b border-gray-200 pb-2 items-center justify-between">
        <div className="flex flex-row gap-2 items-center">
          <Typography
            variant="bodySmall"
            style={{
              background: dateIsToday
                ? JoonColorExpanded.indigo100
                : JoonColorExpanded.neutral0,
              borderRadius: SPACING.space1,
              width: SPACING.space5,
              height: SPACING.space5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {date.format("D")}
          </Typography>
          <Typography variant="bodySmallBold">{date.format("dddd")}</Typography>
        </div>
      </div>
      {orderedRoutines && orderedRoutines.length > 0 ? (
        <div className="flex flex-col gap-2 pt-2">
          <div className="flex flex-row">
            <div className="min-w-5 w-5 mr-2"></div>
            <Typography
              variant="bodyXSmall"
              color={JoonUIColor.text.secondary}
              style={{ width: "56px" }}
            >
              My time
            </Typography>
            <Typography variant="bodyXSmall" color={JoonUIColor.text.secondary}>
              Client time
            </Typography>
          </div>
          <div className="flex flex-col">
            {orderedRoutines.map((routine, index) => (
              <div key={index} className="flex flex-col">
                {routine.map((item, index) => {
                  if (item.type === "questGroup") {
                    const questGroup = item.data as QuestInstance[]
                    return (
                      <QuestListItem
                        key={questGroup[0].id}
                        questGroup={questGroup}
                        isFirstInRoutine={index === 0}
                      />
                    )
                  } else if (item.type === "scheduledMessage") {
                    const scheduledMessage =
                      item.data as ScheduledMessageWithRoutine
                    return (
                      <ScheduledMessageItem
                        key={scheduledMessage.id}
                        scheduledMessage={scheduledMessage}
                        isFirstInRoutine={index === 0}
                      />
                    )
                  }
                  return null
                })}
                {index !== orderedRoutines.length - 1 && (
                  <div className="w-full h-[1px] bg-gray-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-row gap-2 py-1">
          <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
            Nothing planned for today!
          </Typography>
        </div>
      )}
    </div>
  )
}

export default RoutineDate
